import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { acceptInvite as acceptInviteApi } from "@/api/auth";
import { isApiError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/stores/auth";

const inviteSchema = z
  .object({
    password: z.string().min(12),
    passwordConfirm: z.string().min(1),
  })
  .refine((d) => d.password === d.passwordConfirm, {
    path: ["passwordConfirm"],
    message: "mismatch",
  });

type InviteValues = z.infer<typeof inviteSchema>;

export interface AcceptInviteFormProps {
  token: string;
  redirectTo?: string;
}

export function AcceptInviteForm({
  token,
  redirectTo = "/meetings",
}: AcceptInviteFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { password: "", passwordConfirm: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (!token) {
      setFormError(t("acceptInvite.errors.tokenMissing"));
      return;
    }
    try {
      const user = await acceptInviteApi({ token, password: values.password });
      setUser(user);
      await navigate({ to: redirectTo });
    } catch (err) {
      setFormError(mapInviteError(err, t));
    }
  });

  return (
    <Card className="mx-auto w-full max-w-md">
      <CardHeader>
        <CardTitle>{t("acceptInvite.title")}</CardTitle>
        <CardDescription>{t("acceptInvite.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-password">
              {t("acceptInvite.password")}
            </Label>
            <Input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.password ? true : undefined}
              aria-describedby="invite-password-hint"
              {...register("password")}
            />
            <p
              id="invite-password-hint"
              className="text-xs text-muted-foreground"
            >
              {t("acceptInvite.passwordHint")}
            </p>
            {errors.password ? (
              <p role="alert" className="text-sm text-destructive">
                {t("acceptInvite.errors.passwordTooShort")}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invite-password-confirm">
              {t("acceptInvite.passwordConfirm")}
            </Label>
            <Input
              id="invite-password-confirm"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.passwordConfirm ? true : undefined}
              {...register("passwordConfirm")}
            />
            {errors.passwordConfirm ? (
              <p role="alert" className="text-sm text-destructive">
                {t("acceptInvite.errors.passwordMismatch")}
              </p>
            ) : null}
          </div>
          {formError ? (
            <p role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          ) : null}
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting
              ? t("acceptInvite.submitting")
              : t("acceptInvite.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function mapInviteError(err: unknown, t: (k: string) => string): string {
  if (!isApiError(err)) return t("acceptInvite.errors.unknown");
  if (err.code === "network_error") return t("acceptInvite.errors.network");
  if (err.status === 410) return t("acceptInvite.errors.expired");
  if (err.status === 409) {
    // 409 has two distinct meanings in the accept-invite contract; the
    // backend disambiguates via ``detail`` so the operator sees the
    // actual cause instead of a generic "уже принято".
    if (err.code === "employee_already_linked") {
      return t("acceptInvite.errors.employeeAlreadyLinked");
    }
    return t("acceptInvite.errors.alreadyAccepted");
  }
  if (err.status === 404) return t("acceptInvite.errors.notFound");
  return t("acceptInvite.errors.unknown");
}
